import type { LocalizedString } from '../types/work';

export interface ExperienceGroup {
  heading?: LocalizedString;
  bullets: LocalizedString[];
}

export interface ExperienceItem {
  company: LocalizedString;
  role: LocalizedString;
  period: LocalizedString;
  location: LocalizedString;
  groups: ExperienceGroup[];
}

export interface EducationItem {
  school: LocalizedString;
  degree: LocalizedString;
  period: LocalizedString;
  location: LocalizedString;
  notes?: LocalizedString;
}

export const profile = {
  name: { en: 'Li Botian', zh: '李博天' } as LocalizedString,
  tagline: {
    en: 'Technical art & engine tooling',
    zh: '技术美术 & 引擎工具开发',
  } as LocalizedString,
  location: { en: 'Hangzhou, China', zh: '杭州' } as LocalizedString,
  email: 'mail@botianli.com',
  phone: '13776667277',
};

export const intro: LocalizedString = {
  en: "Technical artist and tools engineer. Currently a P7 Technical Art Specialist at Taobao, where I built and lead the Interactive TA team — the people behind the pipelines, shaders, and digital-human systems that turn art briefs into shipping product across Web, Unity, UE, AceNNR, and VisionOS. Previously TD lead at Paper Games (Shining Nikki), and a pipeline R&D engineer at BlueSky Studios on Ice Age and Ferdinand. SCAD MFA in Visual Effects.",
  zh: '技术美术与工具工程师,目前在淘宝担任技术美术专家(P7),从零搭建并管理淘系互动业务的 TA 团队 — 负责把美术需求转化为跨 Web、Unity、UE、AceNNR、VisionOS 等多端的实际产品体验。此前曾在叠纸动画(暖优)担任 TD 技术组组长,在蓝天工作室(BlueSky)参与《冰河世纪》《公牛历险记》的流程研发。SCAD 视效硕士。',
};

export const expertise: LocalizedString[] = [
  {
    en: '3D art industrialization pipelines — DCC plugin suites, USD/glTF flows, asset databases',
    zh: '3D 美术工业化生产管线 — DCC 插件集、USD/glTF 全流程、美术资产数据库',
  },
  {
    en: 'Cross-platform real-time rendering — WebGL2, Unity, UE, AceNNR, VisionOS parity',
    zh: '跨平台实时图形渲染 — WebGL2、Unity、UE、AceNNR、VisionOS 视觉一致性',
  },
  {
    en: 'Digital-human systems — face customization, makeup, costume assembly, physics',
    zh: '数字人核心能力 — 捏脸、妆容、服饰拼装、物理效果',
  },
  {
    en: 'Engine tools & DCC pipelines — Maya / Houdini / Unity / UE',
    zh: '引擎工具与 DCC 管线 — Maya / Houdini / Unity / UE',
  },
  {
    en: 'AIGC 3D content prototyping — Qwen-Image, Trellis 2.0',
    zh: 'AIGC 3D 内容原型探索 — 通义千问 Qwen-Image、Trellis 2.0',
  },
];

export const experiences: ExperienceItem[] = [
  {
    company: {
      en: 'Taobao (Alibaba)',
      zh: '淘宝(中国)软件有限公司',
    },
    role: {
      en: 'Technical Art Specialist (P7), Taobao Business Tech',
      zh: '技术美术专家(P7)淘宝业务技术',
    },
    period: { en: '2021.01 — Present', zh: '2021年01月 — 至今' },
    location: { en: 'Hangzhou', zh: '杭州' },
    groups: [
      {
        heading: { en: 'Core responsibilities', zh: '核心工作职责' },
        bullets: [
          {
            en: 'Coordinated with product, ops, and creative teams across the full lifecycle of 3D art deliverables — concept, production, and quality acceptance — to keep core business art high-quality and fast-iterating.',
            zh: '业务协同与内容落地:联动产品运营、创意设计团队,全流程把控 3D 美术需求的方案设计、生产落地与效果验收,保障业务核心美术内容的高质量、高效率迭代。',
          },
          {
            en: 'Led end-to-end technical solutions for technical art, deeply integrated with frontend/backend and the in-house engine teams, to crack key problems in cross-platform 3D rendering, asset pipelines, and digital-human capabilities.',
            zh: '技术研发与攻坚:主导技术美术全链路技术方案设计与研发落地,深度联动前后端、自研引擎团队,攻克跨平台 3D 渲染、资产管线、数字人核心能力等关键技术难题。',
          },
          {
            en: 'Built the Taobao Interactive TA team from zero — task breakdown, technical R&D, hiring, and a career-growth framework.',
            zh: '团队搭建与管理:从零到一搭建淘系互动业务技术美术(TA)团队,统筹团队工作拆解、技术攻坚、人才培养与职业成长体系建设。',
          },
        ],
      },
      {
        heading: {
          en: '3D art industrialization pipeline',
          zh: '3D 美术工业化生产管线全链路搭建',
        },
        bullets: [
          {
            en: "Designed and shipped Taobao Life's full 3D art industrialization pipeline — Electron client, plugin suites for Maya/Houdini and other DCCs, USD (internal collaboration) and glTF (final delivery) custom import/export plugins, an art-asset database, and an end-to-end production / validation / ingest / publish flow built on Alibaba's internal infrastructure. Used by 20+ internal artists and external ISVs; supports >10,000 garment assets ingested.",
            zh: '主导设计并落地淘宝人生专属 3D 美术工业化全流程解决方案,覆盖 Electron 客户端、Maya/Houdini 等多 DCC 软件插件集、USD(美术内部协同流转)与 glTF(终端最终交付)的全流程自定义导入 / 导出插件体系、美术资产数据库设计,以及使用阿里内部前后端基建,实现资产生产、校验、入库、发布全流程。内部 20+ 3D 美术人员及外部 ISV 服务商使用,支撑累计超万件服饰资产入库。',
          },
        ],
      },
      {
        heading: {
          en: 'Cross-platform rendering & material systems',
          zh: '跨平台图形渲染与材质体系研发',
        },
        bullets: [
          {
            en: 'Authored and optimized skin SSS, garment, and hair materials on WebGL2 and UE, covering both PBR-realistic and stylized variants. Achieved unified asset format and visual parity across WebGL, Unity, UE, AceNNR, and VisionOS so users and artists see consistent results everywhere.',
            zh: '基于 WebGL2 与 UE,完成人体 SSS、服饰、头发等材质的效果编写与性能优化,覆盖 PBR 写实与二次元双风格体系;实现 WebGL、Unity、UE、AceNNR、VisionOS 五大平台的资产格式统一与视觉效果高度还原,保障用户及美术同学的体验一致性。',
          },
          {
            en: "Led art-style definition and core asset production for the Taobao Life Small House project; designed and shipped an offline rendering pipeline on Houdini Renderman that produces lightweight global-illumination references for the WebGL runtime, balancing fidelity and performance on the web.",
            zh: '主导淘宝人生小屋项目美术风格定义与核心资产制作,设计并落地基于 Houdini Renderman 的离线渲染管线,实现 WebGL 端轻量级场景全局光照(GI)效果与性能平衡,保障场景在 Web 端的高质量呈现与流畅运行。',
          },
        ],
      },
      {
        heading: {
          en: 'Digital-human core capabilities',
          zh: '数字人核心能力全栈技术方案设计与落地',
        },
        bullets: [
          {
            en: "Designed and shipped Taobao Life's full digital-human capability stack from zero — face customization, makeup, costume assembly, physics, and clipping prevention — for fully customizable virtual avatars.",
            zh: '从零到一设计并落地淘宝人生数字人全核心能力体系,覆盖捏脸、妆容、服饰拼装、物理效果、防穿模等,打造全功能的虚拟形象自定义能力。',
          },
          {
            en: 'Led the face-customization system end to end: an in-house Maya rigging toolkit, a Houdini-based node graph for blendshape compensation editing, a custom interchange format for face-customization data, and integration with the rigging and game-runtime teams — yielding a high-freedom, high-compatibility face-customization capability.',
            zh: '主导捏脸系统全链路方案设计与落地,其中包括自研基于 Maya 的捏脸绑定美术工具集、基于 Houdini 的节点式捏脸动画补偿编辑系统,自定义设计捏脸数据通用格式,联动动画绑定、游戏研发团队完成全流程能力落地,实现高自由度、高兼容性的虚拟形象捏脸能力。',
          },
          {
            en: 'Designed and built the costume-piece free-assembly and real-time compositing system; cracked the core algorithm for runtime UV recompositing across complex pieces; shipped on UE and Web Canvas; supports unlimited mix-and-match of garment sub-shapes and provides engine-side support for AIGC 3D content generation.',
            zh: '设计并开发服饰散件自由拼装与实时合成系统,攻克复杂 UV 实时二次合成核心算法,完成 UE 与 Web Canvas 双端功能落地,支撑服饰子版型的无限自由搭配,同时为 AIGC 3D 内容生成提供引擎层技术支撑。',
          },
          {
            en: 'Built a Unity DynamicBone-based physics toolset bridging the Unity editor and the WebGL runtime, so artists can author physics bones visually in editor and see them play in the browser.',
            zh: '开发基于 Unity DynamicBone 的物理工具集,打通 Unity 编辑环境与 WebGL 运行环境的物理数据链路,实现美术可视化编辑 Web 端骨骼链条物理效果。',
          },
          {
            en: 'Led the makeup system end to end — taxonomy, real-time render-target compositing, and the supporting art production pipeline — enabling high-freedom makeup customization with consistent visual quality.',
            zh: '主导妆容系统全方案设计与研发,完成妆容分类体系、实时 RT 合成逻辑开发与配套美术生产管线搭建,实现虚拟形象妆容的高自由度自定义与高质量呈现。',
          },
        ],
      },
      {
        heading: {
          en: 'Frontier tech R&D',
          zh: '前沿技术探索与原型落地',
        },
        bullets: [
          {
            en: 'Led prototype validation and delivery of frontier tech — a full digital-human interactive capability on VisionOS, a three.js WebGPU rendering migration, and several other R&D efforts that became multi-platform tech reserves.',
            zh: '主导前沿技术原型验证与落地,完成 VisionOS 端完整数字人交互能力、threejs WebGPU 渲染能力迁移等多项技术预研与原型开发,为业务多终端拓展提供核心技术储备。',
          },
          {
            en: 'Explored AIGC 3D content production: built a 2D/3D art generation solution on top of Qwen-Image and Trellis 2.0, charting a path for AI-assisted industrial 3D content production.',
            zh: '探索 AIGC 3D 内容生产技术体系,搭建基于通义千问 Qwen-Image 与 Trellis 2.0 的 2D/3D 美术生成解决方案,探索 AI 赋能 3D 内容工业化生产的可行路径。',
          },
        ],
      },
      {
        heading: { en: 'Team building & management', zh: '团队建设与管理' },
        bullets: [
          {
            en: 'Built the Taobao Interactive TA team from zero — hiring, workflow, task breakdown, and an R&D escalation framework.',
            zh: '从零到一完成淘系互动业务技术美术(TA)团队的搭建与招聘,建立完善的团队工作流程、任务拆解机制与技术攻坚体系。',
          },
          {
            en: "Owned team members' technical growth and career planning, tailoring development plans to each engineer's background and ability.",
            zh: '负责团队成员的技术成长与职业发展规划,根据同学背景及能力不同,制定不同的发展计划。',
          },
          {
            en: 'Deeply involved in 2D/3D art-team hiring and day-to-day management for the Taobao Life project; coordinated 3D art production scheduling and iteration delivery to keep the project running smoothly.',
            zh: '深度参与淘宝人生项目 2D/3D 美术团队的招募与日常管理,统筹 3D 美术内容的生产排期与迭代落地,保障项目正常运行。',
          },
        ],
      },
    ],
  },
  {
    company: {
      en: 'Nuanyou (Paper Games)',
      zh: '暖优(上海)网络科技有限公司',
    },
    role: {
      en: 'TD → Tech Group Lead, Paper Games Animation',
      zh: 'TD ⇒ 技术小组组长 叠纸动画',
    },
    period: { en: '2020.03 — 2020.10', zh: '2020年03月 — 2020年10月' },
    location: { en: 'Shanghai', zh: '上海' },
    groups: [
      {
        bullets: [
          {
            en: 'Built a Houdini USD production pipeline for managing and optimizing art assets in the Shining Nikki project.',
            zh: '搭建基于 Houdini USD 的生产管线,用于闪耀暖暖项目相关美术资源的资产管理及优化。',
          },
          {
            en: "Built a Houdini + OpenCL Groom toolkit, delivered to the asset team for hair simulation in PV/cinematic shots.",
            zh: '研发基于 Houdini + OpenCL 的 Groom 工具集,交付给资产组用于 PV 中的毛发解算。',
          },
          {
            en: 'Helped artists solve day-to-day technical issues — hair rendering, FX simulation, rigging tools, and material problems.',
            zh: '帮助美术同学解决日常美术的其他疑难杂症,尤其是毛发渲染、特效解算、绑定工具、各种材质问题等。',
          },
        ],
      },
    ],
  },
  {
    company: {
      en: 'Zhejiang Shenghuo Films',
      zh: '浙江圣火影视科技有限公司',
    },
    role: { en: 'Technical Co-founder', zh: '技术合伙人' },
    period: { en: '2019.06 — 2020.02', zh: '2019年06月 — 2020年02月' },
    location: { en: 'Hangzhou', zh: '杭州' },
    groups: [
      {
        bullets: [
          {
            en: 'Startup attempt; built art pipelines and tools to support artists in asset production.',
            zh: '创业尝试,负责搭建美术流程、开发美术工具,辅助美术同学进行资产生产。',
          },
        ],
      },
    ],
  },
  {
    company: {
      en: 'Zhejiang Bocai Media',
      zh: '浙江博采传媒有限公司',
    },
    role: { en: 'R&D Engineer', zh: '研发工程师' },
    period: { en: '2018.12 — 2019.05', zh: '2018年12月 — 2019年05月' },
    location: { en: 'Hangzhou', zh: '杭州' },
    groups: [
      {
        bullets: [
          {
            en: 'Built a remote facial-capture system on UE + iOS ARKit + Google Firebase (before UE had official support); shipped in the animated feature Yu the Great.',
            zh: '开发基于 UE + iOS ARKit + Google Firebase 的可远程面部捕捉系统(在 UE 官方还没有支持的情况下),并用于《大禹治水》长篇动画中。',
          },
          {
            en: 'Built art tools and production workflows for virtual idols and virtual cinematography.',
            zh: '开发用于虚拟偶像、虚拟拍摄等所需的美术工具及其生产流程。',
          },
        ],
      },
    ],
  },
  {
    company: {
      en: 'BlueSky Studios, 20th Century Fox',
      zh: 'BlueSky Studios, 20th Century Fox 蓝天工作室',
    },
    role: {
      en: 'Pipeline R&D Engineer / R&D Engineer',
      zh: '流程研发工程师、R&D 工程师',
    },
    period: { en: '2017.06 — 2018.11', zh: '2017年06月 — 2018年11月' },
    location: { en: 'New York', zh: '纽约' },
    groups: [
      {
        bullets: [
          {
            en: 'Worked on Nuke compositing for shots in Ice Age and Ferdinand; built related art tools and refined the compositing pipeline.',
            zh: '参与制作《冰河世纪》、《公牛历险记》电影镜头 Nuke 合成,开发相关美术工具,完善合成流程。',
          },
          {
            en: "Built a UE 4.16 + HTC Vive VR director's design toolkit, used for shot design on Ferdinand.",
            zh: '开发基于 UE 4.16 + HTC Vive 的全套 VR 导演设计工具,并用于《公牛历险记》部分镜头设计。',
          },
        ],
      },
    ],
  },
];

export const education: EducationItem[] = [
  {
    school: {
      en: 'Savannah College of Art and Design',
      zh: '萨凡纳艺术学院 (SCAD)',
    },
    degree: {
      en: 'MFA, Visual Effects — School of Digital Media',
      zh: '视效硕士 — School of Digital Media',
    },
    period: { en: '2015.01 — 2017.06', zh: '2015年01月 — 2017年06月' },
    location: { en: 'Savannah', zh: '萨凡纳' },
    notes: {
      en: 'Top-3 VFX program in North America. Required full-pipeline art craft plus multi-language programming. GPA 3.94.',
      zh: '北美视效相关专业 TOP3,需要很强的美术全链路制作能力 + 多语言编码能力,GPA 3.94。',
    },
  },
];
